
import { useState } from 'react';
import { Menu, Search } from 'lucide-react';
import { PainelCommandPalette } from '@/components/painel/PainelCommandPalette';
import { PainelNotificationsMenu } from '@/components/painel/PainelNotificationsMenu';
import { PainelUserMenu } from '@/components/painel/PainelUserMenu';
import { cn } from '@/lib/utils';

type PainelTopbarProps = {
  onOpenSidebar: () => void;
};

export function PainelTopbar({ onOpenSidebar }: PainelTopbarProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 flex h-16 items-center gap-4 border-b border-white/10 px-4 backdrop-blur-md md:px-8',
          'bg-[#030023]/92 shadow-sm shadow-black/20'
        )}
      >
        <button
          type="button"
          onClick={onOpenSidebar}
          className="rounded-xl p-2 text-blue-100 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
          aria-label="Abrir menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className={cn(
            'group relative min-w-0 flex-1 max-w-xl text-left',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/35 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030023]'
          )}
          aria-label="Buscar no painel"
        >
          <span
            className={cn(
              'pointer-events-none absolute -inset-px rounded-xl opacity-0 transition-opacity duration-300',
              'bg-gradient-to-r from-sky-500/20 via-sky-400/10 to-indigo-500/15 blur-[1px]',
              'group-hover:opacity-70 group-focus-visible:opacity-100'
            )}
            aria-hidden
          />
          <span
            className={cn(
              'relative flex h-10 w-full items-center gap-2.5 rounded-xl border border-white/10',
              'bg-gradient-to-b from-white/[0.07] to-white/[0.03] px-3',
              'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]',
              'transition-all duration-200',
              'group-hover:border-sky-400/30 group-hover:from-white/[0.09] group-hover:to-white/[0.04]',
              'group-focus-visible:border-sky-400/45 group-focus-visible:shadow-[0_0_24px_-8px_rgba(56,189,248,0.35)]'
            )}
          >
            <Search className="h-4 w-4 shrink-0 text-sky-300/55 transition-colors group-hover:text-sky-300/80" />
            <span className="min-w-0 flex-1 truncate font-inter text-sm text-slate-500/90 group-hover:text-slate-400">
              Buscar páginas, guias, powers…
            </span>
            <kbd
              className={cn(
                'hidden shrink-0 items-center gap-0.5 rounded-md border border-white/10 bg-[#030023]/50 px-1.5 py-0.5 sm:inline-flex',
                'font-mono text-[10px] text-slate-500 transition-colors group-hover:border-sky-400/20 group-hover:text-slate-400'
              )}
            >
              <span>Ctrl</span>
              <span>K</span>
            </kbd>
          </span>
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
          <PainelNotificationsMenu />
          <PainelUserMenu />
        </div>
      </header>

      <PainelCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}
